import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  TextField,
} from '@mui/material'
import { DeleteOutline as DeleteIcon, Warning as WarningIcon, Person as PersonIcon } from '@mui/icons-material'
import React from 'react'

interface DeleteRecognitionDialogProps {
  open: boolean
  isDeleting: boolean
  confirmationText: string
  selectedRecognition: any
  relatedRecognitions: any[]
  isRemoveButtonDisabled: boolean
  onClose: () => void
  onConfirm: () => void
  onConfirmationTextChange: (text: string) => void
}

export default function DeleteRecognitionDialog({
  open,
  isDeleting,
  confirmationText,
  selectedRecognition,
  relatedRecognitions,
  isRemoveButtonDisabled,
  onClose,
  onConfirm,
  onConfirmationTextChange,
}: DeleteRecognitionDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-recognition-dialog-title"
      aria-describedby="delete-recognition-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        }
      }}
    >
      <DialogTitle
        id="delete-recognition-dialog-title"
        sx={{
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 600,
        }}
      >
        Confirm Remove Recognition
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%',
            }
          }}
        >
          <strong>Important:</strong> All related user recognitions within the same recognition group will be removed together. This affects all recipients of this recognition event.
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <DialogContentText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
                Recognition ID
              </DialogContentText>
              <DialogContentText sx={{ fontWeight: 600, color: 'text.primary' }}>
                {selectedRecognition?.recognition_id || selectedRecognition?.id || selectedRecognition?.recognition_event_id || 'N/A'}
              </DialogContentText>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <DialogContentText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
                Date
              </DialogContentText>
              <DialogContentText sx={{ fontWeight: 600, color: 'text.primary' }}>
                {selectedRecognition?.date || selectedRecognition?.created_at || 'N/A'}
              </DialogContentText>
            </Paper>
          </Grid>
        </Grid>

        {selectedRecognition?.message && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <DialogContentText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
              Recognition Message
            </DialogContentText>
            <DialogContentText sx={{ fontStyle: 'italic', color: 'text.primary' }}>
              "{selectedRecognition.message}"
            </DialogContentText>
          </Paper>
        )}

        {relatedRecognitions.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <DialogContentText sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
              Recipients to be affected ({relatedRecognitions.length})
            </DialogContentText>
            <Grid container spacing={1.5}>
              {relatedRecognitions.map((recognition: any, index: number) => (
                <Grid item xs={6} key={index}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PersonIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    <DialogContentText sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
                      {recognition.recipient_name || recognition.recipient || 'Unknown User'}
                    </DialogContentText>
                  </div>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        <DialogContentText sx={{ mb: 2, color: 'text.primary' }}>
          To confirm this action, please type <strong>remove</strong> below:
        </DialogContentText>

        <TextField
          fullWidth
          size="small"
          placeholder="Type 'remove' to confirm"
          value={confirmationText}
          onChange={(e) => onConfirmationTextChange(e.target.value)}
          disabled={isDeleting}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 3 }}>
        <Button
          onClick={onClose}
          disabled={isDeleting}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={isRemoveButtonDisabled}
          startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100,
          }}
        >
          {isDeleting ? 'Removing...' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
